import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronLeft,
  Trophy,
  Medal,
  Crown,
  Flame,
  TrendingUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leaderboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentRank, setCurrentRank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`, { withCredentials: true });
      setLeaderboard(response.data.leaderboard || []);
      setCurrentRank(response.data.current_user_rank);
    } catch (error) {
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="h-6 w-6 text-amber-400" />;
    if (rank === 2) return <Medal className="h-6 w-6 text-slate-400" />;
    if (rank === 3) return <Medal className="h-6 w-6 text-amber-600" />;
    return <span className="font-mono text-lg font-bold text-muted-foreground">{rank}</span>;
  };

  const getRankBg = (rank) => {
    if (rank === 1) return 'bg-gradient-to-r from-amber-500/20 to-amber-500/5 border-amber-500/30';
    if (rank === 2) return 'bg-gradient-to-r from-slate-400/20 to-slate-400/5 border-slate-400/30';
    if (rank === 3) return 'bg-gradient-to-r from-amber-600/20 to-amber-600/5 border-amber-600/30';
    return 'border-border';
  };

  const leagueColors = {
    bronze: 'bg-amber-700',
    silver: 'bg-slate-400',
    gold: 'bg-amber-400',
    platinum: 'bg-cyan-400',
    diamond: 'bg-purple-400'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="leaderboard-page">
      {/* Header */}
      <header className="sticky top-0 bg-background/80 backdrop-blur-md border-b border-border z-40">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/dashboard')}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">Leaderboard</h1>
              <p className="text-muted-foreground text-sm">Weekly rankings</p>
            </div>
          </div>
          <Badge className="bg-teal-500">
            <Trophy className="h-4 w-4 mr-1" />
            Top 30
          </Badge>
        </div>
      </header>

      {/* League Info */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card className="bg-gradient-to-r from-teal-500/10 to-amber-500/10 border-teal-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-teal-500 flex items-center justify-center">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold">Your Rank</p>
                  <p className="text-muted-foreground text-sm">
                    {currentRank ? `#${currentRank}` : 'Not ranked yet'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-amber-500" />
                  <span className="font-mono text-xl font-bold">{user?.total_points || 0}</span>
                </div>
                <p className="text-muted-foreground text-sm">Total XP</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboard List */}
      <div className="max-w-2xl mx-auto px-4 pb-8">
        {loading ? (
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <Skeleton key={`skel-${i}`} className="h-16 rounded-xl" />
            ))}
          </div>
        ) : leaderboard.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Trophy className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No leaderboard data yet.</p>
              <p className="text-muted-foreground text-sm">Complete lessons to earn XP!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {leaderboard.map((entry) => {
              const isCurrentUser = entry.user_id === user?.user_id;
              return (
                <Card 
                  key={entry.user_id}
                  className={`transition-all ${getRankBg(entry.rank)} ${
                    isCurrentUser ? 'ring-2 ring-teal-500' : ''
                  }`}
                  data-testid={`rank-${entry.rank}`}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-center gap-4">
                      {/* Rank */}
                      <div className="w-10 flex justify-center">
                        {getRankIcon(entry.rank)}
                      </div>

                      {/* Avatar */}
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={entry.picture} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {entry.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>

                      {/* Name */}
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${isCurrentUser ? 'text-teal-600' : ''}`}>
                          {entry.name}
                          {isCurrentUser && <span className="text-xs ml-2">(You)</span>}
                        </p>
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${leagueColors[entry.league] || ''}`}
                        >
                          {entry.league || 'bronze'}
                        </Badge>
                      </div>

                      {/* XP */}
                      <div className="text-right">
                        <p className="font-mono font-bold">{entry.weekly_xp?.toLocaleString() || 0}</p>
                        <p className="text-xs text-muted-foreground">XP</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
